// ─── Intake Form Models ───────────────────────────────────────────────────────

export interface IntakeFormData {
  // Personal Information
  firstName: string;
  lastName: string;
  gender: GenderType;
  mobile: string;
  email: string;
  dob: string;

  // Health Card
  healthCard: string;

  // Home Address
  address: string;
  city: string;
  province: string;
  postalCode: string;

  // Emergency Contact
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  emergencyContactAltPhone: string;

  // Health Insurance
  insurerName: string;
  policyNumber: string;
  groupNumber: string;
  relationship: RelationshipType;

  // Visit Information
  visitReason: string;
  lastEyeExam: string;
  currentCorrectionType: CorrectionType;
  lensWearDuration: string;

  // Medical History
  isDiabetic: YesNoType;
  diabeticType: DiabeticType;
  bloodSugarLevel: string;
  isPregnant: YesNoNaType;
  isSmoker: YesNoType;
  hasEyeSurgery: YesNoType;
  eyeSurgeryDetails: string;
  hasEyeInjury: YesNoType;
  eyeInjuryDetails: string;
  currentMedications: string;
  medicalConditions: string[];
  otherMedicalConditions: string;
  allergies: string;
  familyEyeConditions: string[];
  otherFamilyEyeConditions: string;
  familyDoctorName: string;
  familyDoctorPhone: string;

  // Vision Symptoms
  visionSymptoms: string[];
  otherVisionSymptoms: string;

  // Consent
  terms: boolean;
}

// ─── Union / Enum Types ───────────────────────────────────────────────────────

export type GenderType        = '' | 'Male' | 'Female' | 'Non-binary' | 'Prefer not to say' | 'Other';
export type RelationshipType  = '' | 'Self' | 'Spouse / Partner' | 'Child' | 'Other';
export type YesNoType         = '' | 'Yes' | 'No';
export type YesNoNaType       = '' | 'Yes' | 'No' | 'N/A';
export type CorrectionType    = '' | 'None' | 'Glasses' | 'Contact Lenses' | 'Both';
export type DiabeticType      = '' | 'Type 1' | 'Type 2' | 'Gestational' | 'Pre-diabetic';

// ─── Error Map ────────────────────────────────────────────────────────────────

export type IntakeFormErrors = Partial<Record<keyof IntakeFormData, boolean>>;

// ─── Constants ────────────────────────────────────────────────────────────────

export const GENDER_OPTIONS: GenderType[] = [
  'Male', 'Female', 'Non-binary', 'Prefer not to say', 'Other',
];

export const CANADIAN_PROVINCES: string[] = [
  'AB', 'BC', 'MB', 'NB', 'NL',
  'NS', 'NT', 'NU', 'ON', 'PE',
  'QC', 'SK', 'YT',
];

export const RELATIONSHIP_OPTIONS: RelationshipType[] = [
  'Self', 'Spouse / Partner', 'Child', 'Other',
];

export const CORRECTION_TYPE_OPTIONS: CorrectionType[] = [
  'None', 'Glasses', 'Contact Lenses', 'Both',
];

export const DIABETIC_TYPE_OPTIONS: DiabeticType[] = [
  'Type 1', 'Type 2', 'Gestational', 'Pre-diabetic',
];

export const LENS_WEAR_DURATION_OPTIONS: string[] = [
  'Less than 1 year', '1–3 years', '3–5 years', '5–10 years', '10+ years',
];

export const MEDICAL_CONDITIONS: string[] = [
  'Hypertension (High Blood Pressure)',
  'Heart Disease',
  'Thyroid Disease',
  'Arthritis',
  'Lupus / Autoimmune Disease',
  'HIV / AIDS',
  'Cancer',
  'Neurological Disorder',
  'Kidney Disease',
  'None of the above',
];

export const FAMILY_EYE_CONDITIONS: string[] = [
  'Glaucoma',
  'Macular Degeneration',
  'Cataracts',
  'Retinal Disease',
  'Diabetic Retinopathy',
  'Keratoconus',
  'Strabismus (Crossed Eyes)',
  'Amblyopia (Lazy Eye)',
  'None of the above',
];

export const VISION_SYMPTOMS: string[] = [
  'Blurry Vision',
  'Double Vision',
  'Eye Pain or Discomfort',
  'Frequent Headaches',
  'Dry or Itchy Eyes',
  'Excessive Tearing',
  'Sensitivity to Light',
  'Floaters or Flashes',
  'Difficulty Seeing at Night',
  'Redness or Swelling',
  'Burning or Stinging',
  'None',
];

// ─── Initial State ────────────────────────────────────────────────────────────

export const INITIAL_FORM_STATE: IntakeFormData = {
  firstName: '',
  lastName: '',
  gender: '',
  mobile: '',
  email: '',
  dob: '',
  healthCard: '',
  address: '',
  city: '',
  province: '',
  postalCode: '',
  emergencyContactName: '',
  emergencyContactRelationship: '',
  emergencyContactPhone: '',
  emergencyContactAltPhone: '',
  insurerName: '',
  policyNumber: '',
  groupNumber: '',
  relationship: '',
  visitReason: '',
  lastEyeExam: '',
  currentCorrectionType: '',
  lensWearDuration: '',
  isDiabetic: '',
  diabeticType: '',
  bloodSugarLevel: '',
  isPregnant: '',
  isSmoker: '',
  hasEyeSurgery: '',
  eyeSurgeryDetails: '',
  hasEyeInjury: '',
  eyeInjuryDetails: '',
  currentMedications: '',
  medicalConditions: [],
  otherMedicalConditions: '',
  allergies: '',
  familyEyeConditions: [],
  otherFamilyEyeConditions: '',
  familyDoctorName: '',
  familyDoctorPhone: '',
  visionSymptoms: [],
  otherVisionSymptoms: '',
  terms: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const toggleArrayItem = (arr: string[], item: string): string[] =>
  arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];

// ─── Validation ───────────────────────────────────────────────────────────────

export const validateForm = (form: IntakeFormData): IntakeFormErrors => {
  const errors: IntakeFormErrors = {};

  if (!form.firstName.trim())            errors.firstName            = true;
  if (!form.lastName.trim())             errors.lastName             = true;
  if (!form.mobile.trim())               errors.mobile               = true;
  if (!form.email.trim())                errors.email                = true;
  if (!form.dob)                         errors.dob                  = true;
  if (!form.emergencyContactName.trim()) errors.emergencyContactName = true;
  if (!form.emergencyContactPhone.trim())errors.emergencyContactPhone= true;
  if (!form.isDiabetic)                  errors.isDiabetic           = true;
  if (!form.isPregnant)                  errors.isPregnant           = true;
  if (!form.isSmoker)                    errors.isSmoker             = true;
  if (!form.hasEyeSurgery)               errors.hasEyeSurgery        = true;
  if (!form.hasEyeInjury)               errors.hasEyeInjury         = true;
  if (!form.terms)                       errors.terms                = true;

  return errors;
};